import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getApiBaseUrl } from '@/lib/api';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = getApiBaseUrl();

/**
 * AuthCallback handles the return from Emergent Google OAuth
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash || '';
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (!sessionIdMatch) {
        console.error('[oauth] Missing session_id in callback hash', {
          path: location.pathname
        });
        toast.error('Authentication failed - no session ID');
        navigate('/login', { replace: true });
        return;
      }

      const sessionId = decodeURIComponent(sessionIdMatch[1]);

      try {
        const body = new URLSearchParams();
        body.set('session_id', sessionId);

        const response = await axios.post(
          `${API}/auth/google/callback`,
          body,
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            }
          }
        );

        const { access_token, user } = response.data;
        await setAuthSession(access_token);

        toast.success(`Welcome, ${user?.name || 'back'}!`);
        navigate('/app', { replace: true });
      } catch (error) {
        console.error('[oauth] Callback exchange failed', {
          status: error?.response?.status,
          path: '/auth/google/callback'
        });
        const message = error.response?.data?.detail || 'Authentication failed';
        toast.error(message);
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [location.hash, location.pathname, navigate, setAuthSession]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center space-y-4">
        <Loader2 className="w-12 h-12 animate-spin text-slate-600 mx-auto" />
        <p className="text-slate-600 font-medium">Completing sign in...</p>
      </div>
    </div>
  );
}

/**
 * Initiates Google OAuth login via Emergent Auth
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export function initiateGoogleLogin() {
  const redirectUrl = window.location.origin + '/app';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}
